import { Loader2, Search } from "lucide-react";
import { useState } from "react";

import { toastApiError } from "@/api/errors";
import type { KbSearchMode, KnowledgeBase } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChunkCard } from "@/features/knowledge-bases/components/ChunkCard";
import { useKbSearch } from "@/features/knowledge-bases/hooks/useKbSearch";

interface Props {
  kb: KnowledgeBase;
}

export function SearchTab({ kb }: Props) {
  const search = useKbSearch(kb.id);
  const [query, setQuery] = useState("");
  const [topK, setTopK] = useState(5);
  const [mode, setMode] = useState<KbSearchMode>("semantic");

  async function handleSearch() {
    const q = query.trim();
    if (!q) return;
    try {
      await search.mutateAsync({ query: q, top_k: topK, mode });
    } catch (err) {
      toastApiError(err);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      void handleSearch();
    }
  }

  return (
    <div className="space-y-4 py-4">
      <Tabs value={mode} onValueChange={(v) => setMode(v as KbSearchMode)}>
        <TabsList>
          <TabsTrigger value="semantic">Semântica (embeddings)</TabsTrigger>
          <TabsTrigger value="lexical">Léxica (full-text)</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="space-y-2">
        <Label htmlFor="kb-search-query">
          {mode === "lexical" ? "Termos exatos" : "Pergunta"}
        </Label>
        <div className="flex gap-2">
          <Input
            id="kb-search-query"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={
              mode === "lexical"
                ? "ex.: 'CNPJ contrato 2024'"
                : "ex.: como gerar relatório mensal?"
            }
            autoComplete="off"
          />
          <Button onClick={handleSearch} disabled={!query.trim() || search.isPending}>
            {search.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            Buscar
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          {mode === "lexical"
            ? "Busca por correspondência exata de termos (Portuguese FTS). Use para nomes próprios, códigos ou citações."
            : "Busca por similaridade semântica (embeddings). Encontra resultados relacionados mesmo sem match exato de palavras."}
        </p>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <Label htmlFor="kb-search-topk">Resultados (top_k)</Label>
          <span className="text-muted-foreground">{topK}</span>
        </div>
        <Slider
          id="kb-search-topk"
          min={1}
          max={50}
          step={1}
          value={[topK]}
          onValueChange={(v) => setTopK(v[0] ?? 5)}
        />
      </div>

      {search.data ? (
        <div className="space-y-3">
          {search.data.results.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhum resultado para "{search.data.query}".
            </p>
          ) : (
            search.data.results.map((hit) => (
              <ChunkCard
                key={hit.chunk_id}
                hit={hit}
                query={search.data.query}
              />
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
